/* ============================================================
   BambuConnectionCard — dfb793af (1cf7d337 part 3): the Bambu Lab
   account card on the provider cabinet profile tab, next to the
   «Мой налог» card. Not connected → CTA that expands into a login
   form (account email + password; the password is used for the
   single login call and never stored). Connected → token-free card
   (account/status/dates) with an expiry countdown driving the
   reconnect CTA, a disconnect confirm (server wipes the token,
   the row stays for history) and the bound-printers panel with
   live online dots. Send-to-print actions on order detail ride the
   part-2 endpoints and land as a follow-up card.
   ============================================================ */

import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../api/client';
import type { BambuConnection, BambuPrinter } from '../types/bambu';
import {
  bambuOnlineCount,
  buildBambuCardView,
  buildBambuPrinterRowView,
  isValidBambuAccount,
  isValidBambuPassword,
  isValidBambuTfaCode,
} from './bambuView';

const inputStyle: CSSProperties = {
  width: '100%',
  height: 48,
  padding: '12px 16px',
  fontSize: 'var(--fs-h3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  outline: 'none',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function BambuConnectionCard() {
  const [connection, setConnection] = useState<BambuConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setConnection(await api.getBambuConnection());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const disconnect = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.disconnectBambu();
      setConfirmDisconnect(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отключить');
    } finally {
      setBusy(false);
    }
  };

  const view = connection ? buildBambuCardView(connection, new Date(), formatDate, formatDateTime) : null;

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)', maxWidth: 560 }}>
      <div>
        <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Bambu Lab</h2>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          Подключите аккаунт Bambu — принтеры появятся в кабинете с онлайн-статусом,
          а подготовленные файлы можно будет отправлять на печать прямо из заказа.
        </p>
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '0 auto' }} />
      ) : view ? (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-xs)',
              padding: 'var(--space-md)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-bg)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
              <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)' }}>Аккаунт Bambu</span>
              <span className={view.badgeClass}>{view.statusLabel}</span>
            </div>
            {view.lines.map((line) => (
              <p key={line.label} style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                {line.label}: <span style={{ color: 'var(--color-text-primary)' }}>{line.value}</span>
              </p>
            ))}
          </div>

          {view.tokenNotice.text && (
            <p style={{ fontSize: 'var(--fs-caption)', color: view.tokenNotice.level === 'expired' ? 'var(--color-danger)' : 'var(--color-primary)' }}>
              {view.tokenNotice.text}
            </p>
          )}

          {view.problemText && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>
              Последняя ошибка: {view.problemText}
            </p>
          )}

          {/* a958628d: 2FA challenge — the emailed code completes the login. */}
          {view.needsVerification && (
            <BambuVerifyForm
              onVerified={() => void load()}
            />
          )}

          {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}

          {confirmDisconnect ? (
            <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', flex: 1, minWidth: 200 }}>
                Отключить аккаунт Bambu? Токен будет удалён, список принтеров пропадёт из кабинета.
              </span>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void disconnect()}>
                {busy ? 'Отключение…' : 'Отключить'}
              </button>
              <button type="button" className="btn btn-tertiary" disabled={busy} onClick={() => setConfirmDisconnect(false)}>
                Отмена
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
              {view.canReconnect && (
                <button type="button" className="btn btn-primary" onClick={() => setFormOpen(true)}>
                  {view.status === 'disconnected' ? 'Подключить снова' : 'Переподключить'}
                </button>
              )}
              {view.canDisconnect && (
                <button type="button" className="btn btn-secondary" onClick={() => setConfirmDisconnect(true)}>
                  Отключить
                </button>
              )}
            </div>
          )}

          {view.showPrinters && (
            <BambuPrintersPanel
              onTokenProblem={() => {
                // The printers call flips the row to Error/expired — reload the card.
                void load();
              }}
            />
          )}
        </>
      ) : (
        <>
          {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', flex: 1, minWidth: 180 }}>
              Аккаунт Bambu не подключён — принтеры не видны в кабинете.
            </span>
            {!formOpen && (
              <button type="button" className="btn btn-primary" onClick={() => setFormOpen(true)}>
                Подключить
              </button>
            )}
          </div>
        </>
      )}

      {formOpen && (
        <BambuConnectForm
          initialAccount={connection?.accountName ?? ''}
          onConnected={() => {
            setFormOpen(false);
            setConfirmDisconnect(false);
            void load();
          }}
          onCancel={() => setFormOpen(false)}
        />
      )}
    </div>
  );
}

/* ── Connect form: account email + password, single login call ── */

function BambuConnectForm({
  initialAccount,
  onConnected,
  onCancel,
}: {
  initialAccount: string;
  onConnected: () => void;
  onCancel: () => void;
}) {
  const [account, setAccount] = useState(initialAccount);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.connectBambu(account.trim(), password);
      onConnected();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось подключить аккаунт');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-sm)',
        padding: 'var(--space-md)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-bg)',
      }}
    >
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
        Логин и пароль аккаунта Bambu. Пароль используется один раз для входа и не сохраняется —
        дальше работает токен на стороне сервера.
      </p>
      <input
        type="email"
        inputMode="email"
        value={account}
        onChange={(e) => setAccount(e.target.value)}
        placeholder="email аккаунта Bambu"
        autoComplete="email"
        style={inputStyle}
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && isValidBambuAccount(account) && isValidBambuPassword(password)) void submit();
        }}
        placeholder="Пароль"
        autoComplete="current-password"
        style={inputStyle}
      />
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
      <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
        <button type="button" className="btn btn-tertiary" disabled={busy} onClick={onCancel}>
          Отмена
        </button>
        <button
          type="button"
          className="btn btn-primary"
          style={{ flex: 1 }}
          disabled={busy || !isValidBambuAccount(account) || !isValidBambuPassword(password)}
          onClick={() => void submit()}
        >
          {busy ? 'Подключение…' : 'Подключить аккаунт'}
        </button>
      </div>
    </div>
  );
}

/* ── a958628d: 2FA code entry — the 6-digit code from the account email ── */

function BambuVerifyForm({ onVerified }: { onVerified: () => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.verifyBambu(code.trim());
      setCode('');
      onVerified();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось подтвердить код');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-sm)',
        padding: 'var(--space-md)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-bg)',
      }}
    >
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
        На почту аккаунта Bambu отправлен код подтверждения. Введите его, чтобы завершить
        подключение — код действует 10 минут, можно перезапросить через «Переподключить».
      </p>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && isValidBambuTfaCode(code)) void submit();
        }}
        placeholder="Код из письма"
        disabled={busy}
        autoFocus
        style={{ ...inputStyle, letterSpacing: 4, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
      />
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
      <button
        type="button"
        className="btn btn-primary"
        disabled={busy || !isValidBambuTfaCode(code)}
        onClick={() => void submit()}
      >
        {busy ? 'Проверка…' : 'Подтвердить код'}
      </button>
    </div>
  );
}

/* ── Printers panel: live online state from GET printers ── */

function BambuPrintersPanel({ onTokenProblem }: { onTokenProblem: () => void }) {
  const [printers, setPrinters] = useState<BambuPrinter[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (isRefresh: boolean) => {
    setLoading(true);
    setError(null);
    try {
      setPrinters(await api.getBambuPrinters());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось получить принтеры');
      // «не подключен»/«истёк» mean the card above just went stale.
      if (isRefresh) onTokenProblem();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(false); }, []);

  const rows = printers?.map(buildBambuPrinterRowView) ?? [];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-sm)',
        padding: 'var(--space-md)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-bg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-sm)' }}>
        <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)' }}>
          Принтеры
          {printers && (
            <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'var(--fw-regular)' }}>
              {' '}
              · онлайн {bambuOnlineCount(printers)} из {printers.length}
            </span>
          )}
        </span>
        <button
          type="button"
          className="btn btn-tertiary"
          style={{ fontSize: 'var(--fs-caption)', padding: '6px 12px' }}
          disabled={loading}
          onClick={() => void load(true)}
        >
          {loading ? 'Обновление…' : 'Обновить'}
        </button>
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '0 auto' }} />
      ) : error ? (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          К аккаунту не привязан ни один принтер — добавьте его в Bambu Handy / Studio.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          {rows.map((row) => (
            <div
              key={row.devIdTail ?? row.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)',
                padding: 'var(--space-xs) var(--space-sm)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-separator)',
              }}
            >
              <span className={row.dotClass} aria-hidden="true" />
              <span style={{ fontSize: 'var(--fs-body)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.name}
              </span>
              {row.modelLabel && (
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{row.modelLabel}</span>
              )}
              <span
                style={{
                  fontSize: 'var(--fs-caption)',
                  color: row.online ? 'var(--color-success)' : 'var(--color-text-secondary)',
                }}
              >
                {row.onlineLabel}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
